import { useEffect, useRef } from 'react';
import { useSearchResultQuery } from '../queries/useSearchResultQuery';
import { Flex, Spinner } from '@banhala/ui-kit';
import { useScrollToFilterList } from 'screens/src/components/hooks/useScrollToFilterList';
import { View, StyleSheet } from 'react-native';
import { AtomicRenderer } from 'shared/src/component';
import { useLogViewEvent } from '../hook/useLogViewEvent';
import type { ListRef } from '@banhala/ui-kit';
import type { IScreensComponent } from 'shared/src/api';

const styles = StyleSheet.create({
  spinnerContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -10 }, { translateY: -10 }],
    zIndex: 1000,
  },
});

type SearchResultListProps = {
  screenName: string;
  query: string;
  nextToken?: string;
  onScrollY?: (scrollY: number) => void;
};

/**
 * 검색 결과 상품 리스트
 * - SearchResult 페이지 전용
 * - 검색어 기반 무한 스크롤 + 필터 연동
 */
export const SearchResultList = ({
  screenName,
  query,
  nextToken,
  onScrollY,
}: SearchResultListProps) => {
  const listRef = useRef<ListRef<IScreensComponent> | null>(null);

  const {
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useSearchResultQuery({
    screenName,
    query,
    nextToken,
  });

  // 지연 발화 대기 중 자동 prefetch — iOS의 willDisplay 기반 auto-loadmore와 동일한 의도
  // (VIEW 이벤트 유실 방지). prefetch 조건 자체는 select에서 계산됨.
  useEffect(() => {
    if (data?.shouldAutoPrefetchNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [data?.shouldAutoPrefetchNextPage, isFetchingNextPage, fetchNextPage]);

  // iOS: MetaFeedReactor.trackViewLoggingEvent → VIEW_{screenName}
  useLogViewEvent(data);

  const itemHeightsRef = useRef<{ [key: number]: number }>({});
  const { filterListIndex, scrollToFilterList } = useScrollToFilterList({
    atomicRendererRef: listRef,
    components: data?.components,
    isFetching,
    itemHeightsRef,
  });

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      {isFetching && (
        <View style={styles.spinnerContainer}>
          <Spinner size={20} />
        </View>
      )}
      <AtomicRenderer
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={listRef}
        filterListIndex={filterListIndex === -1 ? undefined : filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        onScrollY={onScrollY}
      />
    </>
  );
};
